import { PAGES } from '../routes/PAGES';

export const TELEGRAM_START_PARAM_STORAGE_KEY = 'telegramStartParam';
export const TELEGRAM_MANUAL_LOGIN_INTENT_KEY = 'telegramManualLoginIntent';
export const JOIN_START_PARAM_PREFIX = 'join_';
const MATCH_START_PARAM_PREFIX = 'match_';
const USER_START_PARAM_PREFIX = 'u_';
const START_PARAM_CONTACTS = 'contacts';
const START_PARAM_PARTICIPANTS = 'participants';
const START_PARAM_PROFILE = 'profile';
const START_PARAM_OPEN = 'open';
const START_PARAM_CHATS = 'chats';
const START_PARAM_MANAGE_CHATS = 'manage-chats';
const CHATS_START_PARAM_PREFIX = 'chats_';
const TELEGRAM_START_PARAM_QUERY_KEYS = ['tgWebAppStartParam', 'startapp', 'start_param'];
const QUEUE_PATHS = new Set([PAGES.MANAGE_CHATS, PAGES.ADMIN_CHATS]);

/**
 * telegram-web-app.js always creates window.Telegram.WebApp, including in a normal browser.
 * Only signed initData or Telegram launch hash means we are actually inside a Mini App.
 */
export const isTelegramMiniAppEnvironment = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    const webApp = window.Telegram?.WebApp;
    if (typeof webApp?.initData === 'string' && webApp.initData.trim() !== '') {
        return true;
    }
    if (webApp?.initDataUnsafe?.user) {
        return true;
    }
    const hash = window.location.hash || '';
    return hash.includes('tgWebAppData') || hash.includes('tgWebAppStartParam');
};

const getStartParamFromSearchParams = (searchParams) => {
    for (const key of TELEGRAM_START_PARAM_QUERY_KEYS) {
        const value = searchParams.get(key);
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
    }
    return '';
};

const decodeTelegramParam = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
};

export const getTelegramStartParam = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    const webAppStartParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    const storedStartParam = sessionStorage.getItem(TELEGRAM_START_PARAM_STORAGE_KEY) || '';
    const searchParam = getStartParamFromSearchParams(new URLSearchParams(window.location.search));
    const hashParam = getStartParamFromSearchParams(new URLSearchParams(window.location.hash.replace('#', '')));

    const startParam = webAppStartParam || storedStartParam || searchParam || hashParam;
    const normalizedStartParam = decodeTelegramParam(startParam).trim();

    if (normalizedStartParam) {
        sessionStorage.setItem(TELEGRAM_START_PARAM_STORAGE_KEY, normalizedStartParam);
    }

    return normalizedStartParam;
};

export const getTelegramStartParamContext = () => {
    if (typeof window === 'undefined') {
        return {
            startParam: '',
            hasLiveStartParam: false,
        };
    }

    const webAppStartParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
    const searchParam = getStartParamFromSearchParams(new URLSearchParams(window.location.search));
    const hashParam = getStartParamFromSearchParams(new URLSearchParams(window.location.hash.replace('#', '')));
    const storedStartParam = sessionStorage.getItem(TELEGRAM_START_PARAM_STORAGE_KEY) || '';

    const hasLiveStartParam = Boolean(webAppStartParam || searchParam || hashParam);
    const startParam = decodeTelegramParam(webAppStartParam || searchParam || hashParam || storedStartParam).trim();

    if (startParam) {
        sessionStorage.setItem(TELEGRAM_START_PARAM_STORAGE_KEY, startParam);
    }

    return {
        startParam,
        hasLiveStartParam,
    };
};

export const markTelegramManualLoginIntent = () => {
    if (typeof window === 'undefined') {
        return;
    }
    sessionStorage.setItem(TELEGRAM_MANUAL_LOGIN_INTENT_KEY, 'true');
};

export const resolveTelegramStartParamRedirect = (rawStartParam) => {
    const startParam = decodeTelegramParam(rawStartParam).trim();
    if (!startParam) {
        return null;
    }

    if (startParam.startsWith(JOIN_START_PARAM_PREFIX)) {
        const eventSlug = startParam.slice(JOIN_START_PARAM_PREFIX.length).trim();
        return eventSlug ? `/event/${eventSlug}` : null;
    }

    if (startParam.startsWith(MATCH_START_PARAM_PREFIX)) {
        const matchId = startParam.slice(MATCH_START_PARAM_PREFIX.length).trim();
        return matchId ? `/contacts?matchId=${matchId}` : '/contacts';
    }

    if (startParam.startsWith(USER_START_PARAM_PREFIX)) {
        const userSlug = startParam.slice(USER_START_PARAM_PREFIX.length).trim();
        return userSlug ? `/u/${userSlug}` : null;
    }

    if (startParam.startsWith(`${START_PARAM_CONTACTS}?`)) {
        return `/${startParam}`;
    }

    if (startParam === START_PARAM_CONTACTS) {
        return '/contacts';
    }

    if (startParam === START_PARAM_PARTICIPANTS) {
        return '/participants';
    }

    if (startParam === START_PARAM_PROFILE) {
        return '/profile';
    }

    if (startParam === START_PARAM_MANAGE_CHATS || startParam.startsWith(`${START_PARAM_MANAGE_CHATS}/`)) {
        return PAGES.ADMIN_CHATS;
    }

    if (startParam.startsWith(CHATS_START_PARAM_PREFIX)) {
        const conversationId = startParam.slice(CHATS_START_PARAM_PREFIX.length).trim();
        return conversationId ? `/chats/${conversationId}` : '/chats';
    }

    if (startParam === START_PARAM_CHATS) {
        return '/chats';
    }

    if (startParam === START_PARAM_OPEN) {
        return null;
    }

    // Legacy fallback: old event links may still send only event slug.
    return `/event/${startParam}`;
};

const pathWithoutSearch = (path) => (path || '').split('?')[0];

export const isTelegramStartParamTargetReached = (currentPathWithSearch, redirectPath) => {
    if (currentPathWithSearch === redirectPath) {
        return true;
    }
    const currentPath = pathWithoutSearch(currentPathWithSearch);
    const targetPath = pathWithoutSearch(redirectPath);
    return QUEUE_PATHS.has(currentPath) && QUEUE_PATHS.has(targetPath);
};
